import { useEffect, useState, useCallback } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { CheckCircle2, Loader2, ShieldCheck, Receipt, AlertCircle } from "lucide-react";

const money = (n) => `$${(Number(n) || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function PayInvoice() {
  const { invoiceId } = useParams();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [inv, setInv] = useState(null);
  const [state, setState] = useState("loading"); // loading | ready | paid | verifying | error
  const [paying, setPaying] = useState(false);
  const [err, setErr] = useState("");

  const fetchInv = useCallback(async () => {
    try {
      const { data } = await api.get(`/public/invoices/${invoiceId}`);
      setInv(data);
      return data;
    } catch (e) {
      setState("error");
      return null;
    }
  }, [invoiceId]);

  const verify = useCallback(async () => {
    setState("verifying");
    for (let i = 0; i < 8; i++) {
      try {
        const { data } = await api.get(`/public/payments/status/${sessionId}`);
        if (data.payment_status === "paid") { await fetchInv(); setState("paid"); return; }
      } catch (e) { /* keep polling */ }
      await new Promise((r) => setTimeout(r, 1800));
    }
    const d = await fetchInv();
    setState(d && d.paid ? "paid" : "ready");
  }, [sessionId, fetchInv]);

  useEffect(() => {
    (async () => {
      if (sessionId) { await verify(); return; }
      const d = await fetchInv();
      if (d) setState(d.paid ? "paid" : "ready");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [invoiceId]);

  const pay = async () => {
    setPaying(true); setErr("");
    try {
      const { data } = await api.post(`/public/invoices/${invoiceId}/checkout`, { origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      setPaying(false);
      setErr(e.response?.data?.detail || "Could not start checkout. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-[#111210] text-white flex items-center justify-center p-4" data-testid="pay-invoice-page">
      <div className="w-full max-w-md bg-[#1b1c19] border border-white/10 rounded-2xl overflow-hidden shadow-2xl">
        <div className="px-6 py-5 border-b border-white/10 flex items-center gap-3">
          <span className="w-9 h-9 grid place-items-center rounded-lg bg-[#FFC43D]/15 border border-[#FFC43D]/30"><Receipt className="w-4 h-4 text-[#FFC43D]" /></span>
          <div>
            <p className="text-sm font-semibold leading-tight">{inv?.business_name || "Invoice"}</p>
            <p className="text-xs text-slate-400">{inv?.number ? `Invoice ${inv.number}` : "Secure payment"}</p>
          </div>
        </div>

        <div className="p-6">
          {state === "loading" && <Center><Loader2 className="w-6 h-6 animate-spin text-slate-400" /></Center>}
          {state === "error" && (
            <Center>
              <AlertCircle className="w-8 h-8 text-rose-400 mb-2" />
              <p className="text-sm text-slate-300" data-testid="pay-error">Invoice not found or no longer available.</p>
            </Center>
          )}
          {state === "verifying" && (
            <Center>
              <Loader2 className="w-6 h-6 animate-spin text-[#FFC43D] mb-2" />
              <p className="text-sm text-slate-300">Confirming your payment…</p>
            </Center>
          )}
          {state === "paid" && inv && (
            <div className="text-center py-4" data-testid="pay-success">
              <CheckCircle2 className="w-14 h-14 text-emerald-400 mx-auto mb-3" />
              <p className="text-lg font-semibold">Payment complete</p>
              <p className="text-sm text-slate-400 mt-1">{money(inv.balance_due ?? inv.total)} paid to {inv.business_name}</p>
              <p className="text-xs text-slate-500 mt-4">A receipt has been recorded. You can close this page.</p>
            </div>
          )}
          {state === "ready" && inv && (
            <>
              <p className="text-xs uppercase tracking-wider text-slate-500">Billed to</p>
              <p className="text-sm font-medium mb-4">{inv.client_name}</p>
              <div className="space-y-2 mb-4">
                {(inv.line_items || []).map((li, i) => (
                  <div key={i} className="flex justify-between items-start gap-4 text-sm">
                    <span className="text-slate-300 min-w-0 break-words whitespace-pre-wrap flex-1">{li.service_code ? <span className="font-mono text-slate-400">{li.service_code} · </span> : ""}{li.description || "Item"} <span className="text-slate-500 whitespace-nowrap">× {li.qty}</span></span>
                    <span className="font-mono text-slate-200 text-right whitespace-nowrap shrink-0">{money((Number(li.qty) || 0) * (Number(li.unit_price) || 0))}</span>
                  </div>
                ))}
              </div>
              <div className="border-t border-white/10 pt-4 mb-1 space-y-1">
                {inv.down_payment > 0 && (
                  <>
                    <div className="flex justify-between text-sm text-slate-400"><span>Subtotal</span><span className="font-mono">{money(inv.total)}</span></div>
                    <div data-testid="pay-down-payment" className="flex justify-between text-sm text-emerald-400"><span>Down Payment Applied</span><span className="font-mono">-{money(inv.down_payment)}</span></div>
                  </>
                )}
                <div className="flex items-center justify-between">
                  <span className="text-sm text-slate-400">{inv.down_payment > 0 ? "Balance Due" : "Amount due"}{inv.due_date ? ` · due ${inv.due_date}` : ""}</span>
                  <span className="font-mono text-2xl font-bold text-[#FFC43D]" data-testid="pay-amount">{money(inv.balance_due ?? inv.total)}</span>
                </div>
              </div>
              {inv.memo && <p data-testid="pay-memo" className="italic text-sm text-slate-400 mt-3 whitespace-pre-wrap">{inv.memo}</p>}
              {err && <p className="text-xs text-rose-400 mt-3">{err}</p>}
              <button data-testid="pay-now-btn" onClick={pay} disabled={paying}
                className="w-full mt-5 h-12 bg-white text-[#111210] font-semibold rounded-lg hover:bg-[#FFC43D] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <>Pay {money(inv.balance_due ?? inv.total)}</>}
              </button>
              <p className="flex items-center justify-center gap-1.5 text-[11px] text-slate-500 mt-3">
                <ShieldCheck className="w-3.5 h-3.5" /> Secured by Stripe · cards accepted
              </p>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

function Center({ children }) {
  return <div className="flex flex-col items-center justify-center py-10 text-center">{children}</div>;
}
